import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Plus, Check } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../ModalContainer/ModelContainer'
import SelectDropdownInput from '../../SelectDropdownInput/SelectDropdownInput'
import {
  selectClients,
  selectStatusIsLoadedAll,
  getClientsWithCount,
  selectStatusOfLoading,
} from '../../../reducers/clientsReducer'
import { useAddAttendee } from '../hooks/useAddAttendee'
import { mapClientToOption } from '../utils'

const PAGE_SIZE = 15

const AddAttendeeModal = ({ isVisible, onClose, eventId, onAddSuccess, eventBuildingId }) => {
  const { t } = useTranslation(['attendees', 'common'])
  const dispatch = useDispatch()
  const clients = useSelector(selectClients)
  const isLoadingClients = useSelector(selectStatusOfLoading)
  const loadedAllClients = useSelector(selectStatusIsLoadedAll)
  const [selectedClient, setSelectedClient] = useState(null)
  const { addAttendee, isLoading: isAdding } = useAddAttendee()

  const handleSelectClient = (client) => setSelectedClient(client)

  const fetchClients = async (query = '', isScrolling = false) => {
    const page = isScrolling ? Math.floor(clients.length / PAGE_SIZE) + 1 : 1

    if (isLoadingClients) {
      return
    }
    try {
      await dispatch(
        getClientsWithCount(
          page,
          eventBuildingId,
          undefined,
          query,
          PAGE_SIZE,
          'current',
          isScrolling,
        ),
      )
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (eventBuildingId) {
      fetchClients('', false, eventBuildingId)
    }
  }, [eventBuildingId])

  const handleAdd = async () => {
    if (!selectedClient || !eventId) return

    try {
      await addAttendee(eventId, selectedClient.value)
      setSelectedClient(null)
      if (onAddSuccess) {
        onAddSuccess()
      }
      onClose()
    } catch (error) {
      console.error(error)
    }
  }

  const handleClose = () => {
    setSelectedClient(null)
    onClose()
  }

  const clientsOptions = clients.map(mapClientToOption)
  const selectedValueLabel = selectedClient ? selectedClient.label : ''

  return (
    <ModalContainer
      isVisible={isVisible}
      onClose={handleClose}
      headerIcon={<Plus size={20} />}
      headerTitle={t('attendees:addModal.title')}
      additionalClass="add-attendee-modal"
    >
      <div className="add-attendee-modal__content">
        <div className="add-attendee-modal__search">
          <h3 className="add-attendee-modal__title">{t('attendees:addModal.selectResident')}</h3>
          <div className="add-attendee-modal__input-wrapper">
            <label className="add-attendee-modal__label">
              {t('attendees:addModal.chooseAttendee')}
            </label>
            <SelectDropdownInput
              placeholder={t('attendees:addModal.searchPlaceholder')}
              data={clientsOptions}
              selectedValueLabel={selectedValueLabel}
              onSelectOption={handleSelectClient}
              fetchData={(skip, query, isScrolling, buildingId) =>
                fetchClients(query, isScrolling, buildingId)
              }
              isLoadedAll={loadedAllClients}
              dataToRefetch={eventBuildingId}
              showSelectedInDropdown={true}
            />
          </div>

          <p className="add-attendee-modal__info">{t('attendees:addModal.info')}</p>
        </div>

        <div className="add-attendee-modal__actions">
          <button
            className="button button-transparent auto-width"
            onClick={handleClose}
            disabled={isAdding}
          >
            {t('common:actions.cancel')}
          </button>
          <button
            className="button button-primary auto-width"
            onClick={handleAdd}
            disabled={!selectedClient || isAdding}
          >
            {t('attendees:addModal.add')}
            <Check size={20} />
          </button>
        </div>
      </div>
    </ModalContainer>
  )
}

AddAttendeeModal.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onAddSuccess: PropTypes.func,
  buildingId: PropTypes.number,
}

export default AddAttendeeModal
